"use client"

import { useState, useEffect, useMemo } from "react"
import { Link } from "react-router-dom"
import {
  format,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  getDay,
  isToday,
  isSameMonth,
  differenceInDays,
  addDays,
  subMonths,
  addMonths,
  parseISO,
} from "date-fns"
import { leaveService, holidayService, userService } from "../services/api.service"
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from "../components/ui/card"
import { Button } from "../components/ui/button"
import { Select, SelectOption } from "../components/ui/select"
import AppLayout from "../layouts/AppLayout"
import { useAuth } from "../contexts/AuthContext"
import { Download, Calendar, ChevronLeft, ChevronRight, RefreshCw, Briefcase, Cake, Gift, Activity, PlaneTakeoff } from "lucide-react"

const CalendarPage = () => {
  const { user } = useAuth()
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [leaveRequests, setLeaveRequests] = useState([])
  const [holidays, setHolidays] = useState([])
  const [employees, setEmployees] = useState([])
  const [birthdays, setBirthdays] = useState([])
  const [leaveTypes, setLeaveTypes] = useState([]) // Added to track leave types
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")
  const [selectedDepartment, setSelectedDepartment] = useState("all")
  const [isDownloading, setIsDownloading] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  // Fetch calendar data
  useEffect(() => {
    const fetchCalendarData = async () => {
      try {
        setLoading(true)
        setError("")

        // Prepare date range for the month
        const monthStart = startOfMonth(currentMonth)
        const monthEnd = endOfMonth(currentMonth)
        const startDate = format(monthStart, "yyyy-MM-dd")
        const endDate = format(monthEnd, "yyyy-MM-dd")

        // Fetch leave requests, holidays, employees, and leave types
        const [leaveRes, holidayRes, employeeRes, leaveTypesRes] = await Promise.all([
          user.role === "admin" || user.role === "manager"
            ? leaveService.getAllLeaveRequests()
            : leaveService.getUserLeaveRequests(),
          holidayService.getHolidaysInRange(startDate, endDate),
          user.role === "admin" || user.role === "manager"  
          ? userService.getAllUsers() 
          : userService.getBirthdays(),
          leaveService.getLeaveTypes() // Fetch leave types to identify sick leave
        ])

        // Filter active (approved) leave requests
        const approvedLeaves = (leaveRes.leaveRequests || []).filter((leave) => leave.status === "approved")

        setLeaveRequests(approvedLeaves)
        setHolidays(holidayRes.holidays || [])
        setLeaveTypes(leaveTypesRes.leaveTypes || [])

        // Process employees for birthdays
        if (employeeRes.users) {
          setEmployees(employeeRes.users)

          // Extract birthdays for the current month
          const birthdays = employeeRes.users
            .filter((emp) => emp.birthdate)
            .map((emp) => {
              const birthdate = new Date(emp.birthdate)
              // Create a date for this year's birthday
              const thisYearBirthday = new Date(currentMonth.getFullYear(), birthdate.getMonth(), birthdate.getDate())

              return {
                id: emp.id,
                name: emp.name,
                date: thisYearBirthday,
                department: emp.department,
              }
            })
            .filter((birthday) => isSameMonth(birthday.date, currentMonth))

          setBirthdays(birthdays)
        }

        setLoading(false)
      } catch (err) {
        console.error("Error fetching calendar data:", err)
        setError("Failed to load calendar data")
        setLoading(false)
      }
    }

    fetchCalendarData()
  }, [currentMonth, user.role, refreshKey])

  // Function to check if a leave is sick leave
  const isSickLeave = (leave) => {
    // Check by is_sick_leave flag first
    if (leave.is_sick_leave === 1 || leave.is_sick_leave === true) {
      return true
    }
    
    // Check by leave type name
    const sickLeaveTypes = leaveTypes.filter(type => 
      type.name && type.name.toLowerCase().includes('sick')
    )
    const sickLeaveTypeIds = sickLeaveTypes.map(type => type.id)
    
    return sickLeaveTypeIds.includes(Number(leave.leave_type_id))
  }

  // Navigate to previous month
  const prevMonth = () => {
    setCurrentMonth(subMonths(currentMonth, 1))
  }

  // Navigate to next month
  const nextMonth = () => {
    setCurrentMonth(addMonths(currentMonth, 1))
  }

  // Navigate to current month
  const goToToday = () => {
    setCurrentMonth(new Date())
  }

  // Force refresh of calendar data
  const refreshCalendar = () => {
    setRefreshKey(prev => prev + 1)
  }

  // Change department filter
  const handleDepartmentChange = (e) => {
    setSelectedDepartment(e.target.value)
  }

  // Generate days for the calendar - memoized for performance
  const daysInMonth = useMemo(() => eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth),
  }), [currentMonth])

  // Get day names
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]

  // Get blank days before start of month
  const firstDayOfMonth = getDay(startOfMonth(currentMonth))
  const blankDays = Array.from({ length: firstDayOfMonth }, (_, i) => i)

  // Check if a date has any events
  const getDateEvents = (date) => {
    const formattedDate = format(date, "yyyy-MM-dd")

    // Check holidays
    const holidaysOnDate = holidays.filter((holiday) => {
      const holidayDate = holiday.date instanceof Date ? 
        format(holiday.date, "yyyy-MM-dd") : 
        format(parseISO(holiday.date), "yyyy-MM-dd")
      return holidayDate === formattedDate
    })

    // Check leave requests - separate sick leave from regular leave
    const leavesOnDate = leaveRequests.filter((leave) => {
      // Convert string dates to Date objects
      const startDate = new Date(leave.start_date)
      const endDate = new Date(leave.end_date)
      
      // Set hours to noon to avoid timezone issues
      startDate.setHours(12, 0, 0, 0)
      endDate.setHours(12, 0, 0, 0)
      
      // Convert current date for comparison
      const currentDate = new Date(formattedDate)
      currentDate.setHours(12, 0, 0, 0)
      
      // Filter by department if selected
      if (selectedDepartment !== "all" && leave.department !== selectedDepartment) {
        return false
      }

      // Check if current date is between start and end dates (inclusive)
      return currentDate >= startDate && currentDate <= endDate
    })

    // Separate sick leave from regular leave
    const regularLeaves = leavesOnDate.filter(leave => !isSickLeave(leave))
    const sickLeaves = leavesOnDate.filter(leave => isSickLeave(leave))

    // Check birthdays
    const birthdaysOnDate = birthdays.filter((birthday) => {
      // Filter by department if selected
      if (selectedDepartment !== "all" && birthday.department !== selectedDepartment) {
        return false
      }

      return format(birthday.date, "MM-dd") === format(date, "MM-dd")
    })

    return {
      holidays: holidaysOnDate,
      leaves: regularLeaves,
      sickLeaves: sickLeaves,
      birthdays: birthdaysOnDate,
      hasEvents: holidaysOnDate.length > 0 || leavesOnDate.length > 0 || birthdaysOnDate.length > 0,
    }
  }

  // Get unique departments
  const departments = useMemo(() => {
    return ["all", ...new Set(employees.map((emp) => emp.department).filter(Boolean))]
  }, [employees])

  // Function to download calendar as CSV
  const downloadCalendarAsCSV = () => {
    try {
      setIsDownloading(true)
      setError("")

      // Prepare CSV header
      let csvContent = "Date,Type,Person,Details,Days,Approved By\n"

      // Create sorted array of all events
      const allEvents = []

      // Add holidays
      holidays.forEach((holiday) => {
        if (selectedDepartment === "all" || selectedDepartment === "holidays") {
          const date = new Date(holiday.date)
          if (!isNaN(date.getTime())) {
            allEvents.push({
              date,
              type: "Holiday",
              person: holiday.name || "Unnamed Holiday",
              details: "-",
              days: 1,
              approvedBy: "System",
              sortDate: date,
            })
          }
        }
      })

      // Add leave requests
      leaveRequests.forEach((leave) => {
        if (selectedDepartment === "all" || leave.department === selectedDepartment) {
          const startDate = new Date(leave.start_date)
          const endDate = new Date(leave.end_date)

          if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
            // Calculate number of days
            const days = differenceInDays(addDays(endDate, 1), startDate)

            // Determine leave type for CSV
            const leaveType = isSickLeave(leave) ? "Sick Leave" : "Leave"

            allEvents.push({
              date: startDate,
              endDate: endDate,
              type: leaveType,
              person: leave.user_name || "Unknown User",
              details: leave.leave_type || "Unknown Type",
              days: days,
              approvedBy: leave.approved_by || "HR",
              sortDate: startDate,
            })
          }
        }
      })

      // Add birthdays
      birthdays.forEach((birthday) => {
        if (selectedDepartment === "all" || birthday.department === selectedDepartment) {
          if (!isNaN(birthday.date.getTime())) {
            allEvents.push({
              date: birthday.date,
              type: "Birthday",
              person: birthday.name || "Unknown Employee",
              details: "-",
              days: 1,
              approvedBy: "N/A",
              sortDate: birthday.date,
            })
          }
        }
      })

      // Sort events by date
      allEvents.sort((a, b) => a.sortDate - b.sortDate)

      // Add events to CSV
      allEvents.forEach((event) => {
        const dateDisplay = event.endDate
          ? `${format(event.date, "dd/MM/yyyy")} - ${format(event.endDate, "dd/MM/yyyy")}`
          : format(event.date, "dd/MM/yyyy")

        csvContent += `"${dateDisplay}","${event.type}","${event.person}","${event.details}",${event.days},"${event.approvedBy}"\n`
      })

      // Create a blob and download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `staff-calendar-${format(currentMonth, "MMM-yyyy")}-${selectedDepartment}.csv`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      setIsDownloading(false)
    } catch (err) {
      console.error("Error generating CSV:", err)
      setError(`Failed to download calendar: ${err.message || "Unknown error"}`)
      setIsDownloading(false)
    }
  }

  // Function to download calendar as HTML
  const downloadCalendarAsHTML = () => {
    try {
      setIsDownloading(true)
      setError("")

      // Create HTML content
      let htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Staff Calendar - ${format(currentMonth, "MMMM yyyy")}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              margin: 20px; 
              line-height: 1.6;
            }
            h1, h2 { 
              color: #333; 
              margin-bottom: 10px;
            }
            h1 { font-size: 24px; }
            h2 { font-size: 18px; color: #555; }
            table { 
              border-collapse: collapse; 
              width: 100%; 
              margin-top: 20px;
              box-shadow: 0 1px 3px rgba(0,0,0,0.1);
            }
            th, td { 
              border: 1px solid #ddd; 
              padding: 10px; 
              text-align: left; 
            }
            th { 
              background-color: #f5f5f5; 
              font-weight: bold;
              position: sticky;
              top: 0;
            }
            tr:nth-child(even) { background-color: #f9f9f9; }
            tr:hover { background-color: #f1f1f1; }
            .holiday { background-color: #ffebee; }
            .leave { background-color: #e8f5e9; }
            .sick-leave { background-color: #fce4ec; }
            .birthday { background-color: #f3e5f5; }
            .days-column {
              text-align: center;
              font-weight: bold;
            }
            .print-button { 
              background-color: #4CAF50; 
              color: white; 
              padding: 10px 15px; 
              border: none; 
              border-radius: 4px; 
              cursor: pointer; 
              margin-top: 20px;
              font-size: 14px;
            }
            .print-button:hover { background-color: #45a049; }
            @media print {
              .print-button { display: none; }
              body { padding: 0; margin: 15px; }
              table { box-shadow: none; }
              tr { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <h1>Staff Calendar - ${format(currentMonth, "MMMM yyyy")}</h1>
          <h2>Department: ${selectedDepartment === "all" ? "All Departments" : selectedDepartment}</h2>
          
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Person</th>
                <th>Details</th>
                <th>Days</th>
                <th>Approved By</th>
              </tr>
            </thead>
            <tbody>
      `

      // Create sorted array of all events
      const allEvents = []

      // Add holidays
      holidays.forEach((holiday) => {
        if (selectedDepartment === "all" || selectedDepartment === "holidays") {
          const date = new Date(holiday.date)
          if (!isNaN(date.getTime())) {
            allEvents.push({
              date,
              type: "Holiday",
              person: holiday.name || "Unnamed Holiday",
              details: "-",
              days: 1,
              approvedBy: "System",
              cssClass: "holiday",
              sortDate: date,
            })
          }
        }
      })

      // Add leave requests
      leaveRequests.forEach((leave) => {
        if (selectedDepartment === "all" || leave.department === selectedDepartment) {
          const startDate = new Date(leave.start_date)
          const endDate = new Date(leave.end_date)

          if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
            // Calculate number of days
            const days = differenceInDays(addDays(endDate, 1), startDate)

            // Determine leave type and CSS class
            const leaveType = isSickLeave(leave) ? "Sick Leave" : "Leave"
            const cssClass = isSickLeave(leave) ? "sick-leave" : "leave"

            allEvents.push({
              date: startDate,
              endDate: endDate,
              type: leaveType,
              person: leave.user_name || "Unknown User",
              details: leave.leave_type || "Unknown Type",
              days: days,
              approvedBy: leave.approved_by || "HR",
              cssClass: cssClass,
              sortDate: startDate,
            })
          }
        }
      })

      // Add birthdays
      birthdays.forEach((birthday) => {
        if (selectedDepartment === "all" || birthday.department === selectedDepartment) {
          if (!isNaN(birthday.date.getTime())) {
            allEvents.push({
              date: birthday.date,
              type: "Birthday",
              person: birthday.name || "Unknown Employee",
              details: "-",
              days: 1,
              approvedBy: "N/A",
              cssClass: "birthday",
              sortDate: birthday.date,
            })
          }
        }
      })

      // Sort events by date
      allEvents.sort((a, b) => a.sortDate - b.sortDate)

      // Add events to table
      allEvents.forEach((event) => {
        const dateDisplay = event.endDate
          ? `${format(event.date, "dd/MM/yyyy")} - ${format(event.endDate, "dd/MM/yyyy")}`
          : format(event.date, "dd/MM/yyyy")

        htmlContent += `
          <tr class="${event.cssClass}">
            <td>${dateDisplay}</td>
            <td>${event.type}</td>
            <td>${event.person}</td>
            <td>${event.details}</td>
            <td class="days-column">${event.days}</td>
            <td>${event.approvedBy}</td>
          </tr>
        `
      })

      // Close HTML
      htmlContent += `
            </tbody>
          </table>
          
          <button class="print-button" onclick="window.print()">Print Calendar</button>
        </body>
        </html>
      `

      // Create a blob and download
      const blob = new Blob([htmlContent], { type: "text/html;charset=utf-8;" })
      const url = URL.createObjectURL(blob)
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", `staff-calendar-${format(currentMonth, "MMM-yyyy")}-${selectedDepartment}.html`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      setIsDownloading(false)
    } catch (err) {
      console.error("Error generating HTML:", err)
      setError(`Failed to download calendar: ${err.message || "Unknown error"}`)
      setIsDownloading(false)
    }
  }

  // Get calendar stats for the month
  const calendarStats = useMemo(() => {
    // Filter leave requests that fall within the current month view
    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(currentMonth)
    
    const leavesInMonth = leaveRequests.filter(leave => {
      const startDate = new Date(leave.start_date)
      const endDate = new Date(leave.end_date)
      
      // Check if the leave overlaps with the current month
      return (startDate <= monthEnd && endDate >= monthStart) &&
             (selectedDepartment === 'all' || leave.department === selectedDepartment)
    })
    
    const regularLeaves = leavesInMonth.filter(leave => !isSickLeave(leave))
    const sickLeaves = leavesInMonth.filter(leave => isSickLeave(leave))
    
    // Filter birthdays for the current month
    const birthdaysInMonth = birthdays.filter(bday => 
      selectedDepartment === 'all' || bday.department === selectedDepartment
    )
    
    const stats = {
      totalHolidays: holidays.length,
      totalLeaves: regularLeaves.length,
      totalSickLeaves: sickLeaves.length,
      totalBirthdays: birthdaysInMonth.length
    }
    
    return stats
  }, [holidays, leaveRequests, birthdays, selectedDepartment, leaveTypes, currentMonth])

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-500 mx-auto mb-4"></div>
            <p className="text-gray-500">Loading calendar data...</p>
          </div>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Calendar header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center">
              <Calendar className="mr-2 h-6 w-6 text-indigo-600" />
              Staff Calendar
            </h1>
            <p className="text-gray-500 mt-1">
              Viewing {format(currentMonth, "MMMM yyyy")}
            </p>
          </div>

          {/* Month navigation */}
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" onClick={prevMonth} className="flex items-center">
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </Button>
            <Button variant="outline" size="sm" onClick={goToToday} className="flex items-center">
              <Calendar className="h-4 w-4 mr-1" />
              Today
            </Button>
            <Button variant="outline" size="sm" onClick={nextMonth} className="flex items-center">
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
            <Button variant="ghost" size="sm" onClick={refreshCalendar} title="Refresh calendar data">
              <RefreshCw className="h-4 w-4" />
              <span className="sr-only">Refresh</span>
            </Button>
          </div>
        </div>

        {/* Month and filters */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center space-y-4 md:space-y-0">
          <div className="flex flex-wrap gap-2 items-center">
            <div className="flex items-center bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-sm">
              <span className="font-medium">{calendarStats.totalHolidays}</span>
              <span className="ml-1">Holidays</span>
            </div>
            <div className="flex items-center bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm">
              <span className="font-medium">{calendarStats.totalLeaves}</span>
              <span className="ml-1">Annual Leave</span>
            </div>
            <div className="flex items-center bg-pink-50 text-pink-700 px-3 py-1 rounded-full text-sm">
              <span className="font-medium">{calendarStats.totalSickLeaves}</span>
              <span className="ml-1">Sick Leave</span>
            </div>
            <div className="flex items-center bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-sm">
              <span className="font-medium">{calendarStats.totalBirthdays}</span>
              <span className="ml-1">Birthdays</span>
            </div>
          </div>

          {(user.role === "admin" || user.role === "manager") && (
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium">Department:</label>
              <Select value={selectedDepartment} onChange={handleDepartmentChange} className="w-44">
                <SelectOption value="all">All Departments</SelectOption>
                {departments
                  .filter((dept) => dept !== "all")
                  .map((dept) => (
                    <SelectOption key={dept} value={dept}>
                      {dept}
                    </SelectOption>
                  ))}
              </Select>
            </div>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-md border border-red-200 flex items-center">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            {error}
          </div>
        )}

        {/* Calendar */}
        <Card className="shadow-sm">
          <CardHeader className="bg-gray-50 pb-3">
            <CardTitle className="flex items-center">
              <Calendar className="h-5 w-5 mr-2 text-gray-500" />
              Leave Calendar
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1 md:gap-2">
              {/* Weekday headers */}
              {weekDays.map((day) => (
                <div key={day} className="p-2 text-center font-medium bg-gray-100 rounded">
                  {day}
                </div>
              ))}

              {/* Blank days */}
              {blankDays.map((day) => (
                <div key={`blank-${day}`} className="h-28 md:h-32 p-2 border bg-gray-50 rounded opacity-50"></div>
              ))}

              {/* Calendar days */}
              {daysInMonth.map((day) => {
                const dateEvents = getDateEvents(day)

                return (
                  <div
                    key={day.toString()}
                    className={`min-h-28 md:min-h-32 p-2 border relative rounded overflow-hidden transition-all duration-200 ${
                      isToday(day) ? "bg-blue-50 border-blue-300 shadow-sm" : ""
                    } ${dateEvents.hasEvents ? "hover:shadow-md hover:border-indigo-200" : ""}`}
                  >
                    {/* Date number */}
                    <div className={`flex justify-between items-center ${isToday(day) ? "font-bold text-blue-700" : ""}`}>
                      <span className={`text-sm h-6 w-6 flex items-center justify-center rounded-full ${isToday(day) ? "bg-blue-100" : ""}`}>
                        {format(day, "d")}
                      </span>
                      {format(day, "EEE") === "Sat" || format(day, "EEE") === "Sun" ? (
                        <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                          {format(day, "EEE")}
                        </span>
                      ) : null}
                    </div>

                    {/* Events container */}
                    <div className="mt-1 space-y-1 text-xs">
                      {/* Holidays */}
                      {dateEvents.holidays.map((holiday) => (
                        <div
                          key={`holiday-${holiday.id || Math.random().toString()}`}
                          className="p-1 rounded bg-red-100 text-red-800 truncate border border-red-200 flex items-center"
                          title={holiday.name}
                        >
                          <PlaneTakeoff className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span className="truncate">{holiday.name}</span>
                        </div>
                      ))}

                      {/* Regular Leave requests */}
                      {dateEvents.leaves.map((leave) => (
                        <div
                          key={`leave-${leave.id || Math.random().toString()}`}
                          className="p-1 rounded bg-green-100 text-green-800 truncate border border-green-200 flex items-center"
                          title={`${leave.user_name} - ${leave.leave_type}`}
                        >
                          <Briefcase className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span className="truncate">{leave.user_name}</span>
                        </div>
                      ))}

                      {/* Sick Leave requests */}
                      {dateEvents.sickLeaves.map((leave) => (
                        <div
                          key={`sick-leave-${leave.id || Math.random().toString()}`}
                          className="p-1 rounded bg-pink-100 text-pink-800 truncate border border-pink-200 flex items-center"
                          title={`${leave.user_name} - Sick Leave`}
                        >
                          <Activity className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span className="truncate">{leave.user_name}</span>
                        </div>
                      ))}

                      {/* Birthdays */}
                      {dateEvents.birthdays.map((birthday) => (
                        <div
                          key={`birthday-${birthday.id || Math.random().toString()}`}
                          className="p-1 rounded bg-purple-100 text-purple-800 truncate border border-purple-200 flex items-center"
                          title={`${birthday.name}'s Birthday`}
                        >
                          <Gift className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span className="truncate">{birthday.name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Legend */}
            <div className="mt-6 flex flex-wrap gap-4">
              <div className="flex items-center bg-gray-50 px-3 py-2 rounded-lg">
                <div className="w-4 h-4 bg-red-100 border border-red-200 rounded mr-2"></div>
                <span className="text-sm">Holidays</span>
              </div>
              <div className="flex items-center bg-gray-50 px-3 py-2 rounded-lg">
                <div className="w-4 h-4 bg-green-100 border border-green-200 rounded mr-2"></div>
                <span className="text-sm">Annual Leave</span>
              </div>
              <div className="flex items-center bg-gray-50 px-3 py-2 rounded-lg">
                <div className="w-4 h-4 bg-pink-100 border border-pink-200 rounded mr-2"></div>
                <span className="text-sm">Sick Leave</span>
              </div>
              <div className="flex items-center bg-gray-50 px-3 py-2 rounded-lg">
                <div className="w-4 h-4 bg-purple-100 border border-purple-200 rounded mr-2"></div>
                <span className="text-sm">Birthdays</span>
              </div>
              <div className="flex items-center bg-gray-50 px-3 py-2 rounded-lg">
                <div className="w-4 h-4 bg-blue-50 border border-blue-300 rounded mr-2"></div>
                <span className="text-sm">Today</span>
              </div>
            </div>
          </CardContent>
          <CardFooter className="bg-gray-50 border-t flex flex-wrap gap-2 justify-end">
            <Button
              variant="outline"
              onClick={downloadCalendarAsCSV}
              className="flex items-center gap-2"
              disabled={isDownloading}
            >
              <Download size={16} />
              {isDownloading ? "Generating..." : "Export to CSV"}
            </Button>
            <Button
              variant="outline"
              onClick={downloadCalendarAsHTML}
              className="flex items-center gap-2"
              disabled={isDownloading}
            >
              <Download size={16} />
              {isDownloading ? "Generating..." : "Export to HTML"}
            </Button>
            <Link to="/leave-requests/new">
              <Button className="bg-indigo-600 hover:bg-indigo-700">Request Leave</Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    </AppLayout>
  )
}

export default CalendarPage